import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  SectionMessage,
  SectionMessageAction,
} from 'app/src/components/SectionMessage';
import {
  useOpenPlanSelectionCallback,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './SelectedRequestsExceedLimitMessage.less';

interface SelectedRequestsExceedLimitMessageProps {
  workspaceId: string;
}

export const SelectedRequestsExceedLimitMessage: FunctionComponent<
  SelectedRequestsExceedLimitMessageProps
> = ({ workspaceId }) => {
  const intl = useIntl();

  const { isEligible } = useFreeTrialEligibilityRules(workspaceId);
  const openPlanSelection = useOpenPlanSelectionCallback(workspaceId, {
    redirect: false,
  });

  const handleUpgradeClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: isEligible ? 'startFreeTrialLink' : 'upgradeLink',
      source: 'workspaceRequestsScreen',
    });
    openPlanSelection();
  }, [openPlanSelection, isEligible]);

  if (dontUpsell()) {
    return (
      <div className={styles.container}>
        <SectionMessage
          appearance="warning"
          title={intl.formatMessage({
            id: 'templates.workspace_join_requests.cant-add-all-selected-people',
            defaultMessage: "Can't add all of the selected people.",
            description: "Can't add all of the selected people.",
          })}
        >
          <FormattedMessage
            id="templates.workspace_join_requests.free-workspaces-limited-to-10-no-upsell"
            defaultMessage="Free Workspaces are limited to 10 members, guests, and pending invitations. Try selecting fewer people who aren't in this Workspace."
            description="Free Workspaces are limited to 10 members"
          />
        </SectionMessage>
      </div>
    );
  }
  const actions = [];
  if (isEligible) {
    actions.push(
      <SectionMessageAction
        key="startFreeTrial"
        href="#"
        onClick={handleUpgradeClick}
      >
        <WithEnterpriseManagedOverride>
          <FormattedMessage
            id="templates.workspace_join_requests.start-free-trial"
            defaultMessage="Start free trial"
            description="Start free trial"
          />
        </WithEnterpriseManagedOverride>
      </SectionMessageAction>,
    );
  } else {
    actions.push(
      <SectionMessageAction key="upgrade" href="#" onClick={handleUpgradeClick}>
        <WithEnterpriseManagedOverride>
          <FormattedMessage
            id="templates.workspace_join_requests.upgrade"
            defaultMessage="Upgrade"
            description="Upgrade"
          />
        </WithEnterpriseManagedOverride>
      </SectionMessageAction>,
    );
  }
  return (
    <div className={styles.container}>
      <SectionMessage
        appearance="warning"
        title={intl.formatMessage({
          id: 'templates.workspace_join_requests.cant-add-all-selected-people',
          defaultMessage: "Can't add all of the selected people.",
          description: "Can't add all of the selected people.",
        })}
        actions={actions}
      >
        <FormattedMessage
          id="templates.workspace_join_requests.free-workspaces-limited-to-10"
          defaultMessage="Free Workspaces are limited to 10 members, guests, and pending invitations. Try selecting fewer people who aren't in this Workspace or upgrade this Workspace for unlimited collaborators."
          description="Free Workspaces are limited to 10 members"
        />
      </SectionMessage>
    </div>
  );
};
